import { useContainerBlockContext, useCustomViewBlockContext } from '@lighthouse/block'
import { FilterBlock } from '@lighthouse/block/src/blocks'
import type { FilterBlockAbstract, FilterBlockItemValue, FilterOption, VariableADTvalue } from '@lighthouse/core'
import { VariableType } from '@lighthouse/core'
import { fieldConvertValue, useApplicationContext, useAtomAction } from '@lighthouse/shared'
import { useAtomValue } from 'jotai'
import React, { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { handleFetchFilterCascadeOptionsAtom, handleFetchFilterOptionsAtom } from '@/atoms/blockRecordsDict/action'
import type { HandleFetchFilterCascadeOptionsPayload, HandleFetchFilterOptionsPayload } from '@/atoms/blockRecordsDict/types'
import { filterBlockCacheAtomFamily } from '@/atoms/storage/state'
import { useCurrentPageContext } from '@/contexts/PageContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { usePageDataQueryVariableLabel } from '@/hooks/usePageDataQueryVariableLabel'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'

interface FilterBlockControllerProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onChange'> {
    blockData: FilterBlockAbstract
}

const FilterBlockController: React.FC<FilterBlockControllerProps> = ({ blockData, ...rest }) => {
    const { scope } = useContainerBlockContext()
    const { record } = useCustomViewBlockContext()
    const {
        config: { breakPoint, noFilterLabel },
        id
    } = blockData
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const { run: handleFetchFilterOptions } = useAtomAction(handleFetchFilterOptionsAtom)
    const { run: handleFetchFilterCascadeOptions } = useAtomAction(handleFetchFilterCascadeOptionsAtom)
    const { pageId } = useCurrentPageContext()
    const dataSourceList = useDataSourceList(appId, envId)
    const { t } = useTranslation()
    const filterCacheParams = { appId, envId, pageId, scope, recordId: record?.id, blockId: id }
    const filterData = useAtomValue(filterBlockCacheAtomFamily(filterCacheParams))
    const parseVariable2Field = useParseVariableWithSubscription(
        breakPoint?.design?.background?.type === 'image' ? breakPoint?.design?.background?.image?.resource : undefined
    )
    const { personOptions, roleOptions, departmentOptions } = useApplicationContext()
    const { run: setFilterData } = useAtomAction(filterBlockCacheAtomFamily(filterCacheParams))
    const allLabel = useMemo(() => {
        return noFilterLabel || t('all')
    }, [noFilterLabel, t])
    const handleFetchTextOptions = useCallback(
        async (params: Omit<HandleFetchFilterOptionsPayload, 'envId' | 'blockId' | 'pageSize'>) => {
            const data = await handleFetchFilterOptions({
                ...params,
                envId,
                blockId: id,
                pageSize: 200
            })
            if (!data) {
                return []
            }
            return data.map(
                item =>
                    ({
                        label: item.content[params.showFieldId]?.value,
                        value: item.content[params.fieldId]?.value
                    } as FilterOption)
            )
        },
        [envId, handleFetchFilterOptions, id]
    )

    const getPageDataQueryVariableLabel = usePageDataQueryVariableLabel()
    const parseVariable2String = useCallback(
        (v: VariableADTvalue | undefined) => {
            const field = parseVariable2Field(v)
            if (!field) {
                if (v?.type === VariableType.PAGE_DATA_QUERY) {
                    return getPageDataQueryVariableLabel(v)
                }
                return ''
            }

            return fieldConvertValue(field, { personOptions, roleOptions, departmentOptions })
        },
        [parseVariable2Field, personOptions, roleOptions, departmentOptions, getPageDataQueryVariableLabel]
    )

    const handleFetchCascadeOptions = useCallback(
        (params: Omit<HandleFetchFilterCascadeOptionsPayload, 'appId' | 'envId' | 'blockId' | 'pageSize'>) => {
            return handleFetchFilterCascadeOptions({
                ...params,
                blockId: id,
                pageSize: 10000
            })
        },
        [handleFetchFilterCascadeOptions, id]
    )

    const handleChange = useCallback(
        (itemId: string, data: FilterBlockItemValue) => {
            setFilterData({ itemId, data })
        },
        [setFilterData]
    )

    return (
        <FilterBlock
            blockData={blockData}
            filterData={filterData || {}}
            dataSourceList={dataSourceList}
            onChange={handleChange}
            noFilterLabel={allLabel}
            onFetchTextOptions={handleFetchTextOptions}
            onFetchCascadeOptions={handleFetchCascadeOptions}
            onRenderLabel={parseVariable2String}
            useParseVariableWithSubscription={useParseVariableWithSubscription}
            {...rest}
        />
    )
}

export default FilterBlockController
